import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Home, ArrowLeft } from 'lucide-react';
import { Link } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';

export default function AdvancedDiagnostics() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentSlide > 0) {
        setCurrentSlide(prev => prev - 1);
      } else if (event.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        setCurrentSlide(prev => prev + 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide]);

  const slides = [
    // Slide 1: Title Slide
    {
      title: "Advanced Diagnostics in Longevity Medicine",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center space-y-8">
          <div className="flex items-center space-x-8 mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full"></div>
            </div>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <div className="w-12 h-12 bg-green-500 rounded-full"></div>
            </div>
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full"></div>
            </div>
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
              <div className="w-12 h-12 bg-orange-500 rounded-full"></div>
            </div>
          </div>
          
          <div className="space-y-6">
            <h1 className="text-4xl font-light text-gray-900 leading-tight">
              Advanced Diagnostics in Longevity Medicine
            </h1>
            <h2 className="text-2xl text-blue-600 font-light max-w-4xl">
              Epigenetics, Metabolomics, Microbiome Analysis, & Proteomics
            </h2>
            <h3 className="text-lg text-gray-600 font-light max-w-4xl">
              A Foundational Guide for Clinicians
            </h3>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mt-8 max-w-4xl">
            <p className="text-gray-700 font-light text-sm">
              Exploring the cutting-edge diagnostic modalities transforming longevity medicine 
              and personalized health optimization
            </p>
          </div>
        </div>
      )
    },

    // Slide 2: The Evolving Landscape of Medicine
    {
      title: "The Shift: From Reactive to Proactive & Preventative",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-red-800 mb-3">Traditional Medicine</h3>
              <p className="text-sm text-gray-700">
                Primarily reactive, disease-centric. Focus on diagnosing and treating established illness.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-blue-800 mb-3">Longevity Medicine</h3>
              <p className="text-sm text-gray-700">
                Proactive, preventative, person-centric. Focus on optimizing healthspan, extending longevity, and enhancing functional status.
              </p>
            </div>
            
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-3">Advanced Diagnostics</h3>
              <p className="text-sm text-gray-700">
                This evolution demands more sophisticated diagnostic tools beyond mere disease identification.
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 400 150" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#3B82F6"/>
                </marker>
              </defs>
              <rect x="10" y="30" width="150" height="60" fill="#FECACA" stroke="#F87171" strokeWidth="2" rx="5"/>
              <text x="85" y="65" textAnchor="middle" className="text-sm font-medium fill-red-800">Reactive Model</text>
              <text x="85" y="80" textAnchor="middle" className="text-xs fill-red-700">(Disease-Centric)</text>

              <line x1="170" y1="60" x2="230" y2="60" stroke="#3B82F6" strokeWidth="3" markerEnd="url(#arrowhead)"/>

              <rect x="240" y="30" width="150" height="60" fill="#BFDBFE" stroke="#3B82F6" strokeWidth="2" rx="5"/>
              <text x="315" y="65" textAnchor="middle" className="text-sm font-medium fill-blue-800">Proactive Model</text>
              <text x="315" y="80" textAnchor="middle" className="text-xs fill-blue-700">(Health Optimization)</text>
              <text x="200" y="110" textAnchor="middle" className="text-sm font-bold fill-gray-800">Paradigm Shift</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 3: Introduction to "Omics" in Longevity Medicine
    {
      title: "\"Omics\": A Multi-Layered View of Biology",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Advanced Diagnostics ("Omics")</h3>
            <p className="text-sm text-gray-700">
              Offer a dynamic perspective on an individual's biology.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">They capture the interplay between</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Genetic predispositions (the blueprint)</li>
              <li>• Environmental exposures</li>
              <li>• Lifestyle choices</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Goal</h3>
            <p className="text-sm text-gray-700">
              Move beyond symptom management to personalized, preventative interventions.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">This lecture covers four key modalities</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Epigenetics</li>
              <li>• Metabolomics</li>
              <li>• Microbiome Analysis</li>
              <li>• Proteomics</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 4: Overview of Advanced Diagnostic Modalities
    {
      title: "At a Glance: Four Pillars of Advanced Diagnostics",
      content: (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Overview of Advanced Diagnostic Modalities</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-2 font-medium">Modality</th>
                    <th className="text-left py-2 px-2 font-medium">Core Focus</th>
                    <th className="text-left py-2 px-2 font-medium">Primary Longevity Implication</th>
                    <th className="text-left py-2 px-2 font-medium">Clinical Relevance Snapshot</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Epigenetics</td>
                    <td className="py-2 px-2">Gene expression changes (no DNA sequence alteration)</td>
                    <td className="py-2 px-2">Biological aging clocks, lifestyle impact on genes</td>
                    <td className="py-2 px-2">Assess biological age, targets for gene modulation</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Metabolomics</td>
                    <td className="py-2 px-2">Small-molecule metabolites (the metabolome)</td>
                    <td className="py-2 px-2">Metabolic health, mito function, oxidative stress, nutrient sensing</td>
                    <td className="py-2 px-2">Identify dysfunction, monitor diet/lifestyle response</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Microbiome</td>
                    <td className="py-2 px-2">Microbial communities & their collective genome</td>
                    <td className="py-2 px-2">Inflammaging, gut barrier, immune modulation, gut-brain</td>
                    <td className="py-2 px-2">Guide gut health interventions, assess dysbiosis</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-2 font-medium">Proteomics</td>
                    <td className="py-2 px-2">Proteins (the proteome), structures, & functions</td>
                    <td className="py-2 px-2">Cellular senescence (SASP), inflammatory status, protein damage</td>
                    <td className="py-2 px-2">Measure senescent burden, assess inflammation</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )
    },

    // Slide 5: Lecture Aims & Objectives
    {
      title: "What We Will Cover Today",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Learning Objectives</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Understand the fundamental mechanisms of epigenetics, metabolomics, microbiome analysis, and proteomics</li>
              <li>• Identify clinically relevant biomarkers from each modality</li>
              <li>• Discuss interpretation of these markers in the context of longevity and health optimization</li>
              <li>• Explore therapeutic applications and monitoring strategies</li>
              <li>• Review key peer-reviewed studies and their implications</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Goal</h3>
            <p className="text-sm text-gray-700">
              Equip you to leverage these tools for enhanced patient healthspan and lifespan.
            </p>
          </div>
        </div>
      )
    },

    // Slide 6: Epigenetics: The Interface of Genes & Environment
    {
      title: "Epigenetics: Beyond the DNA Sequence",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Definition</h3>
            <p className="text-sm text-gray-700">
              Heritable changes in gene expression <strong>without</strong> altering the DNA sequence itself.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Key Characteristics</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Acts as a flexible regulatory layer: dictates how, when, and where genes are expressed</li>
              <li>• <strong>Dynamic & Reversible:</strong> Influenced by development, environment, lifestyle, aging</li>
              <li>• ~20% lifespan variability from genetics, ~80% from gene-environment interactions (largely epigenetic)</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Key Implication</h3>
            <p className="text-sm text-gray-700">
              The epigenome is a prime target for longevity interventions.
            </p>
          </div>
        </div>
      )
    },

    // Slide 7: Key Epigenetic Mechanisms
    {
      title: "How Epigenetics Works: Key Mechanisms",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">1. DNA Methylation</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Addition of a methyl group (CH₃) to cytosine (CpG sites)</li>
              <li>• Catalyzed by DNMTs (uses SAM as methyl donor)</li>
              <li>• Promoter methylation → generally gene silencing</li>
              <li>• Demethylation (TET enzymes) → gene activation</li>
              <li>• "Epigenetic drift" with age: global hypomethylation, promoter hypermethylation</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">2. Histone Modifications</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• DNA is wrapped around histone proteins (chromatin)</li>
              <li>• PTMs (acetylation, methylation, etc.) on histone tails alter chromatin structure & gene accessibility</li>
              <li>• Acetylation (HATs) → open chromatin, active genes</li>
              <li>• Deacetylation (HDACs, Sirtuins - NAD⁺-dependent) → closed chromatin, silenced genes</li>
              <li>• Aging: loss of histones, imbalance of marks (e.g., ↑H3K27me3, ↓H3K9ac)</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 550 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              {/* DNA Methylation */}
              <text x="75" y="20" textAnchor="middle" className="text-sm font-bold fill-gray-800">DNA Methylation</text>
              <line x1="20" y1="50" x2="130" y2="50" stroke="#3B82F6" strokeWidth="4"/>
              <circle cx="50" cy="50" r="6" fill="#EF4444"/>
              <text x="46" y="54" textAnchor="middle" className="text-xs fill-white font-bold">M</text>
              <text x="75" y="70" textAnchor="middle" className="text-xs fill-gray-700">Methyl group (M) on DNA</text>
              <text x="75" y="85" textAnchor="middle" className="text-xs fill-gray-700">Alters gene expression</text>

              {/* Histone Modification */}
              <text x="300" y="20" textAnchor="middle" className="text-sm font-bold fill-gray-800">Histone Modification</text>
              <circle cx="250" cy="60" r="20" fill="#A78BFA"/> {/* Histone octamer */}
              <path d="M250,60 Q220,40 200,60 T150,60" stroke="#3B82F6" strokeWidth="3" fill="none"/> {/* DNA strand */}
              <path d="M250,60 Q280,80 300,60 T350,60" stroke="#3B82F6" strokeWidth="3" fill="none"/>
              <circle cx="230" cy="40" r="5" fill="#FBBF24"/> {/* Acetyl/Methyl mark */}
              <text x="227" y="44" textAnchor="middle" className="text-xs fill-black font-bold">Ac</text>
              <text x="300" y="100" textAnchor="middle" className="text-xs fill-gray-700">Modifications (e.g., Acetyl)</text>
              <text x="300" y="115" textAnchor="middle" className="text-xs fill-gray-700">on histone tails alter DNA accessibility</text>

              {/* NAD+ Connection */}
              <rect x="400" y="40" width="130" height="60" fill="#D1FAE5" rx="5" stroke="#059669"/>
              <text x="465" y="60" textAnchor="middle" className="text-xs fill-gray-700">NAD+ is crucial for</text>
              <text x="465" y="75" textAnchor="middle" className="text-xs fill-gray-700">Sirtuins (HDACs) &</text>
              <text x="465" y="90" textAnchor="middle" className="text-xs fill-gray-700">PARPs (DNA Repair)</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 8: Non-Coding RNAs in Aging
    {
      title: "Regulatory RNAs: lncRNAs & miRNAs",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Non-coding RNAs (ncRNAs)</h3>
            <p className="text-sm text-gray-700">
              Transcribed but not translated into proteins; play crucial regulatory roles.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">1. Long non-coding RNAs (lncRNAs)</h3>
            <p className="text-sm text-gray-700 mb-2">Over 200 nucleotides. Mechanisms: Scaffolds, guides, decoys, enhancers.</p>
            <h4 className="font-medium text-green-700 text-sm mb-2">Examples in aging:</h4>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>TERRA:</strong> Telomere maintenance. Dysregulation → telomere shortening, senescence</li>
              <li>• <strong>H19:</strong> Development, growth, senescence. Altered in aging</li>
              <li>• <strong>MALAT1:</strong> Splicing, gene regulation. Implicated in cardiac aging</li>
              <li>• <strong>HOTAIR:</strong> Recruits PRC2 (gene silencing). Implicated in senescence</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">2. MicroRNAs (miRNAs)</h3>
            <p className="text-sm text-gray-700 mb-2">~22 nucleotides. Post-transcriptional regulation: Bind mRNA → degradation or translational repression.</p>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Dysregulated in aging, influence key longevity pathways (IIS, inflammation, senescence)</li>
              <li>• <strong>Example:</strong> miR-34a often upregulated with age, promotes senescence, cardiac aging</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 9: Epigenetic Clocks: Measuring Biological Age
    {
      title: "Epigenetic Clocks: Quantifying Biological Age",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Epigenetic Clocks</h3>
            <p className="text-sm text-gray-700">
              Algorithms using DNA methylation at specific CpG sites to estimate biological age.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Landmark Clocks</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-green-700 text-sm">Horvath (2013)</h4>
                <p className="text-xs text-gray-600">Multi-tissue, 353 CpGs. Predicts chronological age.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Hannum (2013)</h4>
                <p className="text-xs text-gray-600">Blood-based, 71 CpGs. Predicts chronological age.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">PhenoAge (Levine et al., 2018)</h4>
                <p className="text-xs text-gray-600">Predicts "phenotypic age" from clinical biomarkers + DNAm. Better predicts morbidity/mortality.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">GrimAge (Lu et al., 2019)</h4>
                <p className="text-xs text-gray-600">DNAm surrogates for plasma proteins (GDF-15, PAI-1) & smoking. Robust predictor of lifespan/healthspan.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">DunedinPACE (Belsky et al., 2022)</h4>
                <p className="text-xs text-gray-600">Measures current "Pace of Aging."</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Clinical Utility</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Age Acceleration (Biological Age greater than Chronological Age) leads to increased risk of age-related diseases, mortality</li>
              <li>• Used to assess impact of interventions (e.g., Fahy TRIIM trial showed reversal)</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Considerations</h3>
            <p className="text-sm text-gray-700">
              Technical variability, population diversity, causality vs. correlation (biomarkers vs. drivers). Different clocks capture different aging facets.
            </p>
          </div>
        </div>
      )
    },

    // Slide 10: Epigenetic Biomarkers & Clinical Application
    {
      title: "Clinical Application: Epigenetic Biomarkers",
      content: (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Key Epigenetic Biomarkers & Clinical Use</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-2 font-medium">Biomarker Category</th>
                    <th className="text-left py-2 px-2 font-medium">Example</th>
                    <th className="text-left py-2 px-2 font-medium">Interpretation (Longevity)</th>
                    <th className="text-left py-2 px-2 font-medium">Potential Interventions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Epigenetic Clocks</td>
                    <td className="py-2 px-2">Biological Age (GrimAge, DunedinPACE)</td>
                    <td className="py-2 px-2">Accelerated age = ↑risk. GrimAge predicts healthspan.</td>
                    <td className="py-2 px-2">Lifestyle, Metformin, NAD+ precursors, Senolytics</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Global Methylation</td>
                    <td className="py-2 px-2">LINE-1 hypomethylation</td>
                    <td className="py-2 px-2">Genomic instability, aging.</td>
                    <td className="py-2 px-2">Folate, B12, SAMe (cautiously)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Histone Mods (Research/Tissue)</td>
                    <td className="py-2 px-2">Global H3K9ac, H3K27me3</td>
                    <td className="py-2 px-2">Altered patterns = cellular aging, stem cell dysfunction.</td>
                    <td className="py-2 px-2">HDACi (butyrate), Sirtuin activators (resveratrol, NAD+)</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-2 font-medium">miRNA Profiles (Research)</td>
                    <td className="py-2 px-2">Circulating miR-34a, miR-21</td>
                    <td className="py-2 px-2">Inflammaging, senescence, age-related diseases.</td>
                    <td className="py-2 px-2">Diet, exercise, specific nutraceuticals (resveratrol)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Clinical Application</h3>
            <p className="text-sm text-gray-700">
              Assessing biological age, risk stratification, identifying targets for interventions (e.g., lifestyle, NAD+ precursors, senolytics) to modulate gene expression and potentially reverse age acceleration.
            </p>
          </div>
        </div>
      )
    },

    // Slide 11: Metabolomics: Chemical Fingerprint of Cellular Function
    {
      title: "Metabolomics: The Phenotype's Closest Readout",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Definition</h3>
            <p className="text-sm text-gray-700">
              Comprehensive study of small molecules (metabolites) in a biological system (cells, biofluids, tissues).
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Key Characteristics</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• The metabolome is a <strong>dynamic snapshot</strong> of physiology</li>
              <li>• Reflects downstream output of genomic, transcriptomic, proteomic activities + environmental/dietary/microbial influences</li>
              <li>• Sensitive indicator of health status; changes can occur rapidly</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Key Implication</h3>
            <p className="text-sm text-gray-700">
              Provides real-time functional readout, excellent for tracking responses to interventions.
            </p>
          </div>
        </div>
      )
    },

    // Slide 12: Key Metabolic Pathways & Insights
    {
      title: "What Metabolomics Reveals",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Redox Balance & Energy Status</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>NAD+/NADH Ratio:</strong> Critical indicator of cellular redox state, mitochondrial efficiency. Declines with age/dysfunction</li>
              <li>• Glutathione/GSSG ratio: Antioxidant capacity</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Mitochondrial Function</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Organic acids (TCA cycle intermediates: citrate, succinate)</li>
              <li>• Acylcarnitines (fatty acid β-oxidation)</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Key Metabolic Areas</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Amino Acid Metabolism:</strong> BCAAs (insulin resistance), Tryptophan (serotonin), Methionine/Glycine (methylation/GSH)</li>
              <li>• <strong>Lipid Metabolism:</strong> Fatty acids (omega-3/6), triglycerides, phospholipids (membranes), sphingolipids (ceramides - CVD risk)</li>
              <li>• <strong>Detoxification Pathways:</strong> Phase I & II metabolites</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Diet-Microbiome-Metabolome-Host Health Axis</h3>
            <p className="text-sm text-gray-700">
              Many key metabolites are diet or microbiome-derived (TMAO, SCFAs, Urolithins).
            </p>
          </div>
        </div>
      )
    },

    // Slide 13: Clinically Significant Metabolites in Longevity
    {
      title: "Key Metabolites in Aging & Longevity",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Trimethylamine N-oxide (TMAO)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Gut microbe-derived (from choline, L-carnitine in red meat, eggs)</li>
              <li>• Elevated levels linked to increased CVD risk (promotes endothelial dysfunction, inflammation)</li>
              <li>• Nuanced: also in fish (cardioprotective). Context matters (diet, microbiome, FMO3 genetics)</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Plasmalogens</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Ether phospholipids, endogenous antioxidants. Critical for brain, heart, immune cells</li>
              <li>• Decline with age; significantly reduced in Alzheimer's, Parkinson's</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Sphingolipids (e.g., Ceramides)</h3>
            <p className="text-sm text-gray-700">
              Ceramides: Signaling in inflammation, apoptosis, insulin resistance. Elevated specific ceramides lead to increased CVD, T2D risk.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Longevity-Associated Metabolites</h3>
            <p className="text-sm text-gray-700 mb-2">Identified by Long Life Family Study (LLFS):</p>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Ergothioneine</strong> (mushrooms): potent antioxidant, associated with longevity</li>
              <li>• <strong>Flavones</strong> (celery, parsley), <strong>Salsolinol</strong> (cocoa) at youthful levels in centenarians</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 14: Metabolomic Clocks & Signatures of Aging
    {
      title: "Metabolomic Clocks: Predicting Biological Age",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Metabolomic Clocks</h3>
            <p className="text-sm text-gray-700">
              Analogous to epigenetic clocks, using metabolite profiles to predict age or health status.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Landmark Study: Long Life Family Study (LLFS)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Analyzed plasma from over 2,700 individuals (including centenarians)</li>
              <li>• Identified hundreds of metabolites associated with age, longevity, mortality</li>
              <li>• Highlighted role of essential fatty acids, plant-derived metabolites (ergothioneine, flavones)</li>
              <li>• Developed a "metabolomic clock"</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Critique of Metabolomic Clocks</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Earlier stage of development than epigenetic clocks</li>
              <li>• Causality for many associations needs more research</li>
              <li>• Standardization and generalizability are ongoing challenges</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Strength</h3>
            <p className="text-sm text-gray-700">
              Captures real-time functional status and rapid response to interventions.
            </p>
          </div>
        </div>
      )
    },

    // Slide 15: Metabolomic Biomarkers & Clinical Application
    {
      title: "Clinical Application: Metabolomic Biomarkers",
      content: (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Key Metabolomic Biomarkers & Clinical Use</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-2 font-medium">Biomarker/Class</th>
                    <th className="text-left py-2 px-2 font-medium">Example</th>
                    <th className="text-left py-2 px-2 font-medium">Interpretation (Longevity)</th>
                    <th className="text-left py-2 px-2 font-medium">Potential Interventions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">NAD+ Metabolome</td>
                    <td className="py-2 px-2">NAD+/NADH ratio, NAD+</td>
                    <td className="py-2 px-2">Low ratio/NAD+ = mito dysfunction, decreased sirtuin activity, accelerated aging</td>
                    <td className="py-2 px-2">NAD+ precursors (NMN, NR), exercise, CR</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Organic Acids</td>
                    <td className="py-2 px-2">Lactate, Pyruvate, Citrate</td>
                    <td className="py-2 px-2">Elevated L/P ratio = mito issues. Abnormal TCA = metabolic blocks</td>
                    <td className="py-2 px-2">Mito support (CoQ10), B-vitamins, diet, probiotics</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">TMAO</td>
                    <td className="py-2 px-2">Trimethylamine N-oxide</td>
                    <td className="py-2 px-2">Elevated = increased CVD risk (contextual)</td>
                    <td className="py-2 px-2">Diet (decreased red meat), probiotics, DMB (research), exercise</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-2 font-medium">Plasmalogens</td>
                    <td className="py-2 px-2">Specific species</td>
                    <td className="py-2 px-2">Decreased = aging, oxidative stress, cognitive decline</td>
                    <td className="py-2 px-2">Precursor supplementation (research), antioxidants</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Clinical Application</h3>
            <p className="text-sm text-gray-700">
              Identifying metabolic dysfunction (e.g., mitochondrial, insulin resistance), monitoring response to diet/lifestyle, assessing oxidative stress, guiding targeted nutrient therapy.
            </p>
          </div>
        </div>
      )
    },

    // Slide 16: Microbiome: The Inner Ecosystem's Impact
    {
      title: "The Gut Microbiome: A \"Microbial Organ\"",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Definition</h3>
            <p className="text-sm text-gray-700">
              Trillions of microbes (bacteria, archaea, fungi, viruses) and their collective genome, mainly in the colon.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Roles</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Digestion, vitamin synthesis (K, B-vitamins)</li>
              <li>• Immune development/modulation, pathogen protection</li>
              <li>• Detoxification, gut-brain axis</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Assessment Methods</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>16S rRNA gene sequencing:</strong> "Who is there?" (taxonomic composition)</li>
              <li>• <strong>Shotgun metagenomics:</strong> Higher resolution taxonomy + "What can they do?" (functional potential, genes for metabolic pathways)</li>
              <li>• Shift towards functional analysis is key</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 17: Key Microbial Signatures in Aging
    {
      title: "The Aging Microbiome: Signatures & Dysbiosis",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Diversity (Alpha)</h3>
            <p className="text-sm text-gray-700">
              Richness & evenness. Higher diversity generally = healthier, more resilient gut. Often reduced in aging, but complex in centenarians.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Key Taxa</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Akkermansia muciniphila:</strong> Mucin-degrader. Increases gut barrier, decreases inflammation, increases metabolic health. Often decreased with age, but enriched in healthy aging</li>
              <li>• <strong>Bifidobacteria & Lactobacilli:</strong> Beneficial, SCFA producers. Often decreased with age</li>
              <li>• <strong>Faecalibacterium prausnitzii:</strong> Major butyrate producer. Decreased in IBD, inflammation</li>
              <li>• <strong>Proteobacteria:</strong> Increase often indicates dysbiosis, inflammation</li>
              <li>• <strong>Christensenellaceae:</strong> Associated with leanness, healthy aging, enriched in centenarians</li>
            </ul>
          </div>

          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Dysbiosis in Aging</h3>
            <p className="text-sm text-gray-700 mb-2">Imbalance in composition/function.</p>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Characteristics:</strong> Decreased diversity, decreased beneficial microbes, increased pathobionts/pro-inflammatory microbes</li>
              <li>• <strong>Consequences:</strong> Increased intestinal permeability ("leaky gut"), LPS translocation leading to "Inflammaging"</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 18: Functional Metabolites of the Microbiome
    {
      title: "Microbial Metabolites: The Language of the Gut",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              Impact on host health is primarily via metabolites.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Short-Chain Fatty Acids (SCFAs)</h3>
            <p className="text-sm text-gray-700 mb-2">From fiber fermentation:</p>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Butyrate:</strong> Colonocyte fuel, increases gut barrier, anti-inflammatory, HDAC inhibitor (epigenetic effects)</li>
              <li>• <strong>Propionate & Acetate:</strong> Gut health, host metabolism, immune modulation</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Other Key Metabolites</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Secondary Bile Acids:</strong> Microbial conversion of primary bile acids. Signaling molecules (FXR, TGR5) influencing host metabolism, inflammation</li>
              <li>• <strong>TMAO:</strong> Initial TMA production is microbial</li>
              <li>• <strong>Vitamins:</strong> Synthesis of Vitamin K, B-vitamins (folate, biotin - important for epigenetics)</li>
              <li>• <strong>Indole & derivatives:</strong> From tryptophan. Modulate immunity, gut barrier</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Urolithins</h3>
            <p className="text-sm text-gray-700">
              From dietary ellagitannins (pomegranates, berries, nuts). Promotes mitophagy, mitochondrial health, muscle function. Production is microbiome-dependent.
            </p>
          </div>
        </div>
      )
    },

    // Slide 19: Gut Barrier Integrity & Markers
    {
      title: "Gut Barrier: The Gatekeeper",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Healthy Intestinal Barrier</h3>
            <p className="text-sm text-gray-700">
              Prevents translocation of luminal contents (microbes, LPS, antigens). Compromise ("Leaky Gut") leads to systemic inflammation.
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Markers of Increased Intestinal Permeability</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Zonulin (Serum):</strong> Modulates tight junctions. Elevated levels proposed as marker of increased permeability</li>
              <li>• <strong>Lipopolysaccharide (LPS) & LPS-Binding Protein (LBP) (Serum):</strong> Elevated LPS (endotoxemia) or LBP indicates microbial translocation, gut barrier dysfunction. Strong link to inflammaging, metabolic endotoxemia</li>
              <li>• <strong>Diamine Oxidase (DAO) (Serum):</strong> Degrades histamine. Low DAO leads to histamine intolerance, may reflect gut inflammation/mucosal damage</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Additional Markers</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Stool Markers (Inflammation):</strong> Calprotectin, Lactoferrin (neutrophil markers, increased in IBD)</li>
              <li>• <strong>Stool Markers (Immunity):</strong> Secretory IgA (sIgA)</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 20: Microbiome Markers & Clinical Application
    {
      title: "Clinical Application: Microbiome Markers",
      content: (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Key Microbiome Markers & Clinical Use</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-2 font-medium">Marker Category</th>
                    <th className="text-left py-2 px-2 font-medium">Example</th>
                    <th className="text-left py-2 px-2 font-medium">Interpretation (Longevity)</th>
                    <th className="text-left py-2 px-2 font-medium">Potential Interventions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Diversity (Alpha)</td>
                    <td className="py-2 px-2">Shannon, Chao1 (Stool)</td>
                    <td className="py-2 px-2">Low = dysbiosis, inflammation, decreased resilience</td>
                    <td className="py-2 px-2">High-fiber/plant-rich diet, pre/probiotics</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Key Taxa</td>
                    <td className="py-2 px-2">Akkermansia, F. prausnitzii, Bifidobacterium (Stool)</td>
                    <td className="py-2 px-2">Low beneficials = impaired barrier, decreased SCFAs, increased inflammation</td>
                    <td className="py-2 px-2">Targeted pre/probiotics, dietary changes</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">SCFAs</td>
                    <td className="py-2 px-2">Butyrate, Propionate (Stool)</td>
                    <td className="py-2 px-2">Low butyrate = decreased fiber fermentation, barrier issues, decreased anti-inflammatory</td>
                    <td className="py-2 px-2">Increased dietary fiber, prebiotics, butyrate supplementation (postbiotic)</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-2 font-medium">Barrier Integrity</td>
                    <td className="py-2 px-2">Zonulin, LPS (Serum)</td>
                    <td className="py-2 px-2">Elevated = "leaky gut", systemic inflammation</td>
                    <td className="py-2 px-2">Gut healing (glutamine, zinc), address dysbiosis, diet</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Clinical Application</h3>
            <p className="text-sm text-gray-700">
              Guiding gut health interventions (diet, pre/pro/postbiotics), assessing dysbiosis & inflammation, managing gut permeability, personalizing fiber/polyphenol intake.
            </p>
          </div>
        </div>
      )
    },

    // Slide 21: Proteomics: The Functional Machinery of the Cell
    {
      title: "Proteomics: The Cell's Workhorses",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Definition</h3>
            <p className="text-sm text-gray-700">
              Large-scale study of proteins (the proteome) – their structures, functions, interactions, modifications.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Key Characteristics</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Proteins are the primary functional molecules: enzymes, structural components, signaling molecules, receptors</li>
              <li>• The proteome is highly dynamic, reflecting cellular state, environment, disease</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Key Implication</h3>
            <p className="text-sm text-gray-700">
              Provides direct insight into functional dysregulation and senescent burden.
            </p>
          </div>
        </div>
      )
    },

    // Slide 22: Proteomics in Aging: Senescence & SASP
    {
      title: "Proteomics Unveils Aging Mechanisms",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Cellular Senescence</h3>
            <p className="text-sm text-gray-700">
              Hallmark of aging – irreversible cell cycle arrest.
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Senescence-Associated Secretory Phenotype (SASP)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Senescent cells secrete pro-inflammatory cytokines (IL-6, IL-8), chemokines, growth factors, proteases</li>
              <li>• Contributes to chronic inflammation ("inflammaging"), tissue dysfunction</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Key Senescence-Related Proteins (measurable in plasma/serum)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>GDF-15 (Growth Differentiation Factor 15):</strong> Stress-responsive cytokine. Markedly increased with age, stress, inflammation, disease (CVD, cancer). Robust biomarker of biological aging, mortality risk</li>
              <li>• <strong>NT-proBNP:</strong> Cardiac stress marker, also associated with longevity (complex relationship)</li>
              <li>• <strong>IGFBP-2, Cystatin-C:</strong> SASP-related, change with age, inflammation</li>
              <li>• Other SASP: IL-1β, IL-6, TNF-α, MCP-1</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Clinical Significance</h3>
            <p className="text-sm text-gray-700">
              Proteomics is crucial for identifying and quantifying SASP components.
            </p>
          </div>
        </div>
      )
    },

    // Slide 23: Proteomic Clocks & Pathway Integration
    {
      title: "Proteomic Clocks & Functional Insights",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Proteomic Aging Clocks</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Algorithms using levels of multiple plasma proteins (e.g., via SOMAscan, mass spectrometry) to predict biological age, health status, mortality</li>
              <li>• Often highlight proteins in inflammation, immune response, growth factor signaling</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Pathway Integration</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Maps protein interaction networks and signaling pathways altered in aging</li>
              <li>• Reveals how upstream -omic changes translate to functional protein-level changes</li>
              <li>• Example: Proteomics of senescent cells shows mitochondrial proteome reprogramming (OXPHOS, metabolic enzymes)</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Inflammatory Proteome</h3>
            <p className="text-sm text-gray-700">
              Many key aging biomarkers (GDF-15, ILs, TNF-α, CRP) are part of this, serving as diagnostic and monitoring tools for inflammaging.
            </p>
          </div>
        </div>
      )
    },

    // Slide 24: Proteomic Biomarkers & Clinical Application
    {
      title: "Clinical Application: Proteomic Biomarkers",
      content: (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Key Proteomic Biomarkers & Clinical Use</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-2 font-medium">Biomarker Category</th>
                    <th className="text-left py-2 px-2 font-medium">Example</th>
                    <th className="text-left py-2 px-2 font-medium">Interpretation (Longevity)</th>
                    <th className="text-left py-2 px-2 font-medium">Potential Interventions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Senescence/SASP</td>
                    <td className="py-2 px-2">GDF-15, PAI-1, TIMP-1</td>
                    <td className="py-2 px-2">Elevated = increased senescent burden, pro-inflammatory. High GDF-15 = increased risk</td>
                    <td className="py-2 px-2">Senolytics (Dasatinib+Quercetin, Fisetin), anti-inflammatories</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Inflammatory Cytokines</td>
                    <td className="py-2 px-2">IL-6, TNF-α, hsCRP</td>
                    <td className="py-2 px-2">Elevated = systemic inflammation (inflammaging), driver of aging</td>
                    <td className="py-2 px-2">Anti-inflammatory diet, exercise, omega-3s, curcumin</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Cardiovascular</td>
                    <td className="py-2 px-2">NT-proBNP, hsTroponins, ApoB/ApoA1 ratio</td>
                    <td className="py-2 px-2">NT-proBNP = cardiac stress. ApoB/A1 = atherogenic risk</td>
                    <td className="py-2 px-2">Lifestyle, BP control, lipid therapies</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-2 font-medium">Proteomic Clocks</td>
                    <td className="py-2 px-2">Panels of proteins</td>
                    <td className="py-2 px-2">"Proteomic age" acceleration predicts mortality/disease risk</td>
                    <td className="py-2 px-2">Broad lifestyle interventions, targeted therapies</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Clinical Application</h3>
            <p className="text-sm text-gray-700">
              Assessing senescent cell burden (SASP), quantifying inflammaging, risk stratification for age-related diseases, evaluating senolytic/anti-inflammatory therapies.
            </p>
          </div>
        </div>
      )
    },

    // Slide 25: Cross-Talk & Systems Integration
    {
      title: "The -Omics Web: Interconnected Systems",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Interconnected Systems</h3>
            <p className="text-sm text-gray-700">
              These -omic layers are not isolated; they form a deeply interconnected web.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Metabolism ↔ Epigenetics</h3>
            <p className="text-sm text-gray-700 mb-2">Metabolites are cofactors for epigenetic enzymes:</p>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Acetyl-CoA (from glucose/fat) for HATs</li>
              <li>• SAM (from methionine) for DNMTs, HMTs</li>
              <li>• NAD+ (energy status) for Sirtuins, PARPs</li>
              <li>• α-Ketoglutarate (TCA cycle) for TETs, JmjC demethylases</li>
            </ul>
            <p className="text-sm text-gray-700 mt-2">Epigenetics regulates metabolic gene expression.</p>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Microbiome → Metabolome → Epigenetics</h3>
            <p className="text-sm text-gray-700">
              Gut microbes produce metabolites (SCFAs like butyrate - an HDAC inhibitor; B-vitamins like folate - for SAM cycle) that directly influence host epigenetics and metabolism.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Proteomics as Integrator</h3>
            <p className="text-sm text-gray-700">
              Protein expression/activity is the functional output of gene expression (genetic/epigenetic control) and is modulated by metabolic state (PTMs).
            </p>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <defs>
                <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#6B7280"/>
                </marker>
              </defs>

              <rect x="200" y="20" width="100" height="40" fill="#BFDBFE" stroke="#3B82F6" strokeWidth="2" rx="10"/>
              <text x="250" y="45" textAnchor="middle" className="text-sm font-medium fill-blue-800">Epigenetics</text>

              <rect x="50" y="100" width="100" height="40" fill="#BFDBFE" stroke="#3B82F6" strokeWidth="2" rx="10"/>
              <text x="100" y="125" textAnchor="middle" className="text-sm font-medium fill-blue-800">Metabolomics</text>

              <rect x="350" y="100" width="100" height="40" fill="#BFDBFE" stroke="#3B82F6" strokeWidth="2" rx="10"/>
              <text x="400" y="125" textAnchor="middle" className="text-sm font-medium fill-blue-800">Microbiome</text>

              <rect x="200" y="180" width="100" height="40" fill="#BFDBFE" stroke="#3B82F6" strokeWidth="2" rx="10"/>
              <text x="250" y="205" textAnchor="middle" className="text-sm font-medium fill-blue-800">Proteomics</text>

              <path d="M220,60 Q150,80 130,100" stroke="#6B7280" strokeWidth="2" markerEnd="url(#arrowhead)" fill="none"/>
              <path d="M130,140 Q180,160 220,180" stroke="#6B7280" strokeWidth="2" markerEnd="url(#arrowhead)" fill="none"/>
              <path d="M370,140 Q320,160 280,180" stroke="#6B7280" strokeWidth="2" markerEnd="url(#arrowhead)" fill="none"/>
              <path d="M280,60 Q350,80 370,100" stroke="#6B7280" strokeWidth="2" markerEnd="url(#arrowhead)" fill="none"/>
              <path d="M150,115 Q250,90 350,115" stroke="#6B7280" strokeWidth="2" markerEnd="url(#arrowhead)" fill="none"/>
              <path d="M350,125 Q250,150 150,125" stroke="#6B7280" strokeWidth="2" markerEnd="url(#arrowhead)" fill="none"/>

              <text x="170" y="80" textAnchor="middle" className="text-xs fill-gray-600">SAM, NAD+, Ac-CoA</text>
              <text x="330" y="80" textAnchor="middle" className="text-xs fill-gray-600">Host factors</text>
              <text x="170" y="170" textAnchor="middle" className="text-xs fill-gray-600">Enzyme levels</text>
              <text x="330" y="170" textAnchor="middle" className="text-xs fill-gray-600">SCFAs, Vitamins</text>
              <text x="250" y="120" textAnchor="middle" className="text-xs fill-gray-600">Diet</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 26: The Role of Circadian Rhythms
    {
      title: "Circadian Rhythms: The Master Orchestrator",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Core Clock Genes</h3>
            <p className="text-sm text-gray-700">
              Governed by core clock genes (CLOCK, BMAL1), orchestrates 24-hour rhythms in metabolism, immune function, DNA repair, hormone secretion.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Direct Interactions with -Omics</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Epigenetics:</strong> Rhythmic activity of SIRT1 (NAD+-dependent) influences histone acetylation. DNAm can oscillate</li>
              <li>• <strong>Metabolomics:</strong> Large portion of metabolome (glucose, lipids, AAs) oscillates, driven by clocks & feeding</li>
              <li>• <strong>Microbiome:</strong> Composition and activity display diurnal rhythms, influenced by host feeding & clock. Microbial metabolites provide feedback</li>
              <li>• <strong>Proteomics:</strong> Expression of many enzymes and signaling proteins oscillates</li>
            </ul>
          </div>

          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Impact of Disruption</h3>
            <p className="text-sm text-gray-700">
              Shift work, poor sleep: Impaired NAD+ synthesis, mitochondrial dysfunction, genomic instability, chronic inflammation, metabolic syndrome leading to accelerated aging.
            </p>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Clinical Implication</h3>
            <p className="text-sm text-gray-700">
              Restoring circadian health is foundational for longevity.
            </p>
          </div>
        </div>
      )
    },

    // Slide 27: Clinically Relevant Biomarkers
    {
      title: "Interpreting Biomarkers: Beyond Standard Ranges",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Goal</h3>
            <p className="text-sm text-gray-700">
              Understand underlying processes and functional status, not just "normal" vs. "abnormal."
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Optimal-Functional Ranges</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Longevity medicine aims for ranges reflecting robust health and resilience, often tighter than standard lab ranges</li>
              <li>• Example: hsCRP less than 0.5 mg/L (longevity) vs. less than 3.0 mg/L (standard CVD risk)</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Actionable Biomarkers</h3>
            <p className="text-sm text-gray-700 mb-2">Most valuable are those that:</p>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Indicate biological age/risk</li>
              <li>• Reflect modifiable physiological processes</li>
              <li>• Guide personalized interventions (lifestyle, nutraceuticals, pharma)</li>
              <li>• Serve as objective tools for monitoring therapeutic efficacy</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Integrated View</h3>
            <p className="text-sm text-gray-700">
              Look for patterns and convergences across -omic layers. A multi-system acceleration requires multi-pronged intervention.
            </p>
          </div>
        </div>
      )
    },

    // Slide 28: Therapeutic Interventions: Foundational Lifestyle
    {
      title: "The Cornerstone: Foundational Lifestyle Interventions",
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Diet</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Whole, unprocessed foods; diverse plants (fiber, phytochemicals); healthy fats; quality protein. (Mediterranean, plant-rich)</li>
              <li>• Caloric Restriction (CR), Intermittent Fasting (IF), Time-Restricted Eating (TRE): Activate longevity pathways (AMPK, sirtuins, autophagy), decrease IIS/mTOR</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Exercise</h3>
            <p className="text-sm text-gray-700">
              Aerobic, resistance, flexibility. Increases mitochondrial biogenesis, insulin sensitivity, decreases inflammation, increases BDNF.
            </p>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Sleep</h3>
            <p className="text-sm text-gray-700">
              7-9 hours quality sleep. Crucial for hormonal balance, immune function, cellular repair, glymphatic clearance.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Stress Management & Circadian Optimization</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Stress Management:</strong> Mindfulness, yoga, breathwork. Normalize HPA axis, decrease cortisol, decrease inflammation</li>
              <li>• <strong>Circadian Rhythm Optimization:</strong> Consistent sleep-wake, morning light, evening blue light restriction, timed eating. Synchronizes physiology</li>
            </ul>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-indigo-800 mb-3">Broad Impact</h3>
            <p className="text-sm text-gray-700">
              These exert broad, powerful effects on ALL -omic layers.
            </p>
          </div>
        </div>
      )
    },

    // Slide 29: Targeted Supplementation & Emerging Therapies
    {
      title: "Targeted Interventions: Personalization is Key",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Guiding Principles</h3>
            <p className="text-sm text-gray-700">
              Guided by diagnostic data, clinical presentation, patient goals. Always adjunct to lifestyle.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">NAD+ Precursors (NMN, NR)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Aim:</strong> Boost cellular NAD+ to support sirtuins, PARPs, mitochondrial function</li>
              <li>• <strong>Dosing:</strong> NMN 250-1000mg/day; NR 250-1000mg/day</li>
              <li>• <strong>Monitoring:</strong> NAD+ levels (emerging), clinical response, aging biomarkers</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Senolytics (e.g., Fisetin, Dasatinib + Quercetin)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Aim:</strong> Selectively clear senescent cells to reduce SASP and tissue dysfunction</li>
              <li>• <strong>Fisetin Dosing:</strong> Pulsed, e.g., ~20mg/kg/day for 2-3 days, repeated</li>
              <li>• <strong>Monitoring:</strong> SASP markers (GDF-15), inflammatory markers, clinical improvement</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Microbiome Modulators</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Prebiotics (Inulin, FOS, GOS, resistant starch):</strong> Fuel beneficial bacteria. (5-15g/day, gradual)</li>
              <li>• <strong>Probiotics (Strain-specific: Lactobacillus, Bifidobacterium, Akkermansia):</strong> Confer health benefits</li>
              <li>• <strong>Postbiotics (e.g., Butyrate, Urolithin A):</strong> Direct beneficial metabolites. Urolithin A (from ellagitannins or supplement) for mitophagy</li>
            </ul>
          </div>

          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-3">Other Examples (based on individual needs)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Magnesium (glycinate, malate, threonate): 200-400mg elemental</li>
              <li>• CoQ10 (Ubiquinol): 100-200mg for mito support</li>
              <li>• Omega-3s (EPA/DHA): 1-2g for anti-inflammation</li>
              <li>• Glutathione/precursors (NAC): For antioxidant support</li>
            </ul>
            <p className="text-sm text-gray-700 mt-2 font-medium">
              Always start low, go slow, monitor response and potential interactions.
            </p>
          </div>
        </div>
      )
    },

    // Slide 30: Monitoring & Follow-Up
    {
      title: "The Journey: Monitoring & Iteration",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Ongoing Process</h3>
            <p className="text-sm text-gray-700">
              Longevity medicine is an ongoing process, not a one-time fix.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Regular Re-assessment</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Clinical symptoms and functional status</li>
              <li>• Targeted biomarkers based on initial findings and interventions (e.g., epigenetic clocks, GDF-15, TMAO, NAD+ levels, gut diversity/permeability markers)</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Interpreting Changes</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Look for trends over time</li>
              <li>• Correlate biomarker changes with clinical improvements and lifestyle adherence</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Adjusting Protocols</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Therapies are not static; they evolve with the patient's progress and new data</li>
              <li>• Refine dietary strategies, supplement dosages, exercise routines</li>
            </ul>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-indigo-800 mb-3">Patient Engagement & Goal</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Empower patients with their data to foster adherence and shared decision-making</li>
              <li>• <strong>Goal:</strong> Create a personalized "health dashboard" to navigate towards optimal healthspan</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 31: Conclusion
    {
      title: "Advancing Healthspan: A New Era in Medicine",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Unprecedented Insights</h3>
            <p className="text-sm text-gray-700">
              Epigenetics, metabolomics, microbiome analysis, and proteomics provide unprecedented insights into individual biology and aging.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Paradigm Shift</h3>
            <p className="text-sm text-gray-700">
              These tools enable a shift from reactive disease care to proactive health optimization.
            </p>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Clinical Application Involves</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Comprehensive assessment and risk stratification</li>
              <li>• Personalized, multi-modal interventions (lifestyle, targeted therapies)</li>
              <li>• Continuous monitoring and adaptation</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">The Future</h3>
            <p className="text-sm text-gray-700">
              The future is data-driven, personalized, and focused on maximizing both lifespan and healthspan.
            </p>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-indigo-800 mb-3">Call to Action</h3>
            <p className="text-sm text-gray-700">
              Embrace these advanced diagnostics to empower your patients on their journey to a longer, healthier life.
            </p>
          </div>
        </div>
      )
    },

    // Slide 32: SHIFT Framework for Longevity Medicine
    {
      title: "SHIFT Framework for Longevity Medicine",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-2">A signaling-based model for diagnosing, interpreting, and intervening in aging-related decline</h3>
            <p className="text-sm text-gray-700">Structured around five core biological functions</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold mr-3">S</div>
                <h3 className="text-lg font-medium text-amber-800">Sensing</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The body's ability to perceive internal and external cues (e.g., nutrients, stress, light, redox status).</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> mTOR, AMPK, insulin/IGF-1, sirtuins.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Fasting insulin, HOMA-IR, leptin, glucose variability.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> GLP-1 RAs, intermittent fasting, berberine, myo-inositol, CGMs.</p>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-3">H</div>
                <h3 className="text-lg font-medium text-green-800">Homeostatic Defense</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The maintenance of equilibrium in systems like blood pressure, metabolism, and electrolytes under stress.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> Autophagy, Nrf2, unfolded protein response.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> hs-CRP, homocysteine, GGT, ferritin.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> NAC, sauna, cold exposure, hormetic stressors.</p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mr-3">I</div>
                <h3 className="text-lg font-medium text-red-800">Inflammation</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> Chronic low-grade inflammation that drives aging, immune dysregulation, and tissue dysfunction.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> NF-κB, inflammasome, COX/LOX, PGC-1α.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> hs-CRP, IL-6, TNF-α, omega-6/omega-3 ratio.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Omega-3s, curcumin, HRT, lifestyle medicine, targeted immunomodulation.</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">F</div>
                <h3 className="text-lg font-medium text-purple-800">Fibrosis & Tissue Remodeling</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The degradation or stiffening of tissue architecture via scarring, ECM breakdown, and cellular senescence.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> TGF-β, YAP/TAZ, MMPs.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Fibroscan, elastography, GDF-15, collagen markers.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Rapamycin, senolytics, exosome therapy, regenerative peptides.</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3">T</div>
                <h3 className="text-lg font-medium text-blue-800">Timekeeping & Biorhythms</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The cellular machinery responsible for circadian rhythm, repair, and regenerative capacity.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> CLOCK genes, melatonin signaling, telomerase, NAD+ synthesis.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Cortisol awakening response, melatonin levels, biological age clocks.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Circadian entrainment, melatonin, chronological interventions, qEEG-driven neurotherapy.</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <Link href="/">
              <Home className="w-4 h-4 text-gray-500 hover:text-gray-700" />
            </Link>
            <span className="text-gray-400">/</span>
            <Link href="/mentorship" className="text-gray-600 hover:text-gray-800">
              Mentorship Program
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Advanced Diagnostics</span>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link href="/mentorship" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Mentorship Program
            </Link>
            <LectureDownloader lectureName="Advanced Diagnostics" fileName="AdvancedDiagnostics" />
          </div>
          <h1 className="text-3xl font-light text-gray-900 mb-2">Advanced Diagnostics in Longevity Medicine</h1>
          <p className="text-gray-600 font-light">
            Comprehensive exploration of epigenetics, metabolomics, microbiome analysis, and proteomics
          </p>
        </div>

        {/* Slideshow Container */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Slide Content */}
          <div className="relative">
            <div className="h-[500px] p-8 bg-gradient-to-br from-gray-50 to-white">
              <div className="h-full flex flex-col">
                <h2 className="text-2xl font-light text-gray-900 mb-6 text-center">
                  {slides[currentSlide].title}
                </h2>
                <div className="flex-1 overflow-y-auto">
                  {slides[currentSlide].content}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-light"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 font-light">
                {currentSlide + 1} of {slides.length}
              </span>
            </div>

            {/* Slide Indicators */}
            <div className="flex space-x-1">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full ${
                    index === currentSlide ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-light"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Embedded Audio Lecture */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Accompanying Audio Lecture</h3>
            <p className="text-sm text-gray-600 mt-1">
              Listen to the comprehensive audio guide while navigating through the slides
            </p>
          </div>
          <div className="p-6 flex justify-center">
            <iframe 
              src="https://share.descript.com/embed/qbzJP2KJYor" 
              width="640" 
              height="360" 
              frameBorder="0" 
              allowFullScreen
              className="rounded-lg shadow-sm"
              title="Advanced Diagnostics Audio Lecture"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Keyboard Navigation */}
      <div className="fixed bottom-4 right-4 bg-white p-2 rounded-lg shadow-md">
        <p className="text-xs text-gray-500 font-light">Use ← → arrows to navigate</p>
      </div>
    </div>
  );
}